import { FunctionComponent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { format } from "date-fns";
import { useQuery } from "@apollo/client";
import backArrow from "../../assets/task/back_arrow.svg";
import BreadCumb from "../../components/common/Breadcrumb";
import dashIcon from "../../assets/calendar-svgrepo-com.svg";
import ClockCIcon from "../../assets/clock-svgrepo-com.svg";
import DateSelector from "../../components/common/DateSelector";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import { showError } from "../../components/common/ToastNotification";
import { POD_GOALS_BY_ACTION_TYPE } from "../../api/query";

const STATUS_CLASS: Record<string, string> = {
  "In Progress": "lightblue-statusbg-card",
  "Not Started": "lightgray-statusbg-card",
  Completed: "lightgreen-statusbg-card",
  Testing: "lightyellow-statusbg-card",
  "On Hold": "lightred-statusbg-card",
};

const rolePriority: Record<string, number> = {
  "UI/UX Designer": 0,
  "Frontend Developer": 1,
  "Backend Developer": 2,
  QA: 3,
  DevOps: 4,
  "CS Ops": 5,
};

const GoalDashPodDetail: FunctionComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pod_id, pod_name, pod_type,action_type } = (location.state as any) ?? {};
  const today = format(new Date(), "yyyy-MM-dd");
  const [startDate, setStartDate] = useState<string | null>(today);
  const [endDate, setEndDate] = useState<string | null>(today);

  const { control } = useForm<any>();

  const { data, loading, error } = useQuery(POD_GOALS_BY_ACTION_TYPE, {
    fetchPolicy: "no-cache",
    variables: {
      pod_id: pod_id ? parseInt(pod_id) : undefined,
      start_date: startDate ?? undefined,
      end_date: endDate ?? undefined,
    },
    skip: !pod_id || !startDate || !endDate,
    onError: (err) => showError(err.message),
  });

const actionTypeGroups: any[] =
  data?.pod_goals_by_action_type?.action_type_groups ?? [];

const normalizedActionTypeGroups = actionTypeGroups.map((group: any) => ({
  ...group,
  goals: [
    ...(group.goals ?? []).map((goal: any) => ({
      ...goal,
      goal_type: "normal",
    })),
  ],
}));

  const actionTypePriority: Record<string, number> = {
    Bug: 0,
    "Follow up": 1,
    "Training Issue": 2,
    Enhancement: 3,
    Other: 4,
  };

const sortedActionTypeGroups = [...normalizedActionTypeGroups].sort((a, b) => {
  const aPriority =
    actionTypePriority[a.action_type] ?? Number.MAX_SAFE_INTEGER;

  const bPriority =
    actionTypePriority[b.action_type] ?? Number.MAX_SAFE_INTEGER;

  return aPriority - bPriority;
});

  const filteredActionTypeGroups = action_type
    ? sortedActionTypeGroups.filter((g) => g.action_type === action_type)
    : sortedActionTypeGroups;

  return (
    <section
      id="cts-respon-p-dv"
      className="w-full relative overflow-hidden flex flex-row items-start gap-6 justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope"
    >
      {/* Breadcrumb + Header */}
      <div className="self-stretch grid flex-row items-center justify-start w-full gap-[5px] max-w-full">
        <BreadCumb />
        <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
          <img
            className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer top-[0px] dark-white-img"
            alt=""
            src={backArrow}
            onClick={() => navigate(-1)}
          />
          <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
            Pod Details
          </h3>
        </div>
      </div>

      {/* Top info panel */}
      <div className="goalsdash-main-dv flex self-stretch flex-col min-w-full m-0 rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove items-start justify-start p-8 mq575:p-3 box-border gap-[16px] max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
        {/* Pod identity row */}
        <div className="topnav-dash-item-dv grid gap-2 items-center min-w-full mq825:grid">
          <div className="flex gap-2 items-center mq825:grid">
            <div className="flex gap-2 items-center">
              <img src={dashIcon} className="w-5 h-5 object-cover" alt="" />
              <h5 className="my-0 text-xl-dv self-stretch relative text-lg dark:text-white">
                {pod_name ?? "Pod"}
              </h5>
            </div>
          </div>
        </div>

        {/* Date filters */}
        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 items-end gap-[16px] min-w-full">
          <Controller
            name="start_date"
            control={control}
            render={({ field }) => (
              <DateSelector
                label="Start Date"
                selectedDate={startDate}
                onChange={(date: any) => {
                  field.onChange(date);
                  setStartDate(
                    date ? format(new Date(date), "yyyy-MM-dd") : null,
                  );
                   setEndDate("");
                }}
                nothideParsedValue={true}
                blueIcon={true}
              />
            )}
          />
          <Controller
            name="end_date"
            control={control}
            render={({ field }) => (
              <DateSelector
                label="End Date"
                selectedDate={endDate}
                onChange={(date: any) => {
                    if (startDate && new Date(date) < new Date(startDate)) {
                    showError("End date cannot be earlier than start date.");
                    return;
                  }
                  field.onChange(date);

                  setEndDate(
                    date ? format(new Date(date), "yyyy-MM-dd") : null,
                  );
                }}
                nothideParsedValue={true}
                blueIcon={true}
              />
            )}
          />
        </div>
      </div>

      {/* Goals panel */}
      <div className="goalsdash-main-dv flex self-stretch flex-col min-w-full m-0 rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
        {loading ? (
          <div className="w-full">
            <SkeletonLoader count={5} height="h-28" className="mb-3" />
          </div>
        ) : error ? (
          <div className="self-stretch rounded-lg overflow-hidden flex flex-col items-center justify-center py-[60px] bg-slate-100 dark:bg-slate-700">
            <p className="text-sm text-red-500">
              Failed to load goals. Please try again.
            </p>
          </div>
        ) : filteredActionTypeGroups.length === 0 ? (
          <div className="self-stretch rounded-lg overflow-hidden flex flex-col items-center justify-center py-[60px] bg-slate-100 dark:bg-slate-700">
            <p className="text-sm dark:text-white">No goals available</p>
          </div>
        ) : (
          <div className="grid self-stretch flex-col gap-[24px] max-w-full">
            {filteredActionTypeGroups.map((group: any, gi: number) => (
              <div key={gi} className="flex flex-col gap-[16px]">
                {/* Action type section header */}
                <div className="flex self-stretch w-full border-b border-gray-200 dark:border-slate-600 pb-2">
                  <p className="my-0 text-sm font-semibold dark:text-white">
                    {group.action_type}
                    <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                      {group.goals?.length ?? 0} goal
                      {group.goals?.length !== 1 ? "s" : ""}
                    </span>
                  </p>
                </div>

                {/* Goal cards */}
                <div className="grid self-stretch flex-col gap-[16px] max-w-full lightgray-bg-poddetail">
                  {(group.goals ?? []).map((goal: any, gi2: number) => (
                    <div
                      key={goal.id ?? gi2}
                      className="goaldash-card-item-dv p-4 grid gap-4 cursor-pointer"
                      onClick={() =>
                        navigate("/goal_pod_detail", {
                        state: {
                          goal_id: goal.id,
                          goal_type: goal.goal_type,
                        },
                      })
                      }
                    >
                      {/* Title */}
                      <div className="goals_dash_pod_detail flex gap-4 items-center justify-between mq991:grid mq991:justify-start">
                        <h5 className="my-0 text-xl-dv self-stretch relative text-lg dark:text-white">
                          {goal.title}
                        </h5>
                        {/* Status + Outcome badges */}
                        <div className="flow gap-2 flex-wrap mq630:flex mq825:gap-1">
                          {goal.goal_status && (
                            <span
                              className={
                                STATUS_CLASS[goal.goal_status] ??
                                "lightgray-statusbg-card"
                              }
                            >
                              {goal.goal_status}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Dates row */}
                      {(goal.start_date || goal.end_date) && (
                        <div className="flex flex-wrap gap-4">
                          {goal.start_date && (
                            <p className="flex items-center my-0 text-sm dark:text-white gap-1">
                              <img
                                src={ClockCIcon}
                                className="w-4 h-4 object-cover"
                                alt=""
                              />
                              Start:{" "}
                              {format(
                                new Date(goal.start_date),
                                "MMM dd, yyyy",
                              )}
                            </p>
                          )}
                          {goal.end_date && (
                            <p className="flex items-center my-0 text-sm dark:text-white gap-1 redtext-pod-card">
                              <img
                                src={ClockCIcon}
                                className="w-4 h-4 object-cover"
                                alt=""
                              />
                              End:{" "}
                              {format(new Date(goal.end_date), "MMM dd, yyyy")}
                            </p>
                          )}
                        </div>
                      )}

{/* lightgreen-statusbg-card */}
{/* lightblue-statusbg-card */}
                      <div className="flex flex-wrap gap-1 items-center">
                        {
                          goal?.defect?.symptom && 
                               <span className="lightred-statusbg-card">{goal?.defect?.symptom}</span>
                   
                        }
                           {
                          goal?.defect?.churn_quote && 
                               <span className="lightred-statusbg-card">{goal?.defect?.churn_quote}</span>
                   
                        }
                            {
                            goal?.defect?.request_type && 
                                                 <span className="lightblue-statusbg-card">{goal?.defect?.request_type}</span>
                          }
      
                         {/* <span className="lightyellow-statusbg-card">lightblue status card</span>
                          <span className="lightgray-statusbg-card">lightblue status card</span> */}
                      </div>

                      {/* Members */}
                      {Array.isArray(goal.goal_members) &&
                        goal.goal_members.length > 0 && (
                          <div>
                            <ul className="m-0 pl-5">
                              {[...goal.goal_members]
                                .sort((a: any, b: any) => {
                                  const aPriority =
                                    rolePriority[a.role] ??
                                    Number.MAX_SAFE_INTEGER;
                                  const bPriority =
                                    rolePriority[b.role] ??
                                    Number.MAX_SAFE_INTEGER;

                                  return aPriority - bPriority;
                                })
                                .map((member: any, mi: number) => (
                                  <li
                                    key={member.employee_id ?? mi}
                                    className="text-sm dark:text-white"
                                  >
                                    {member.member_name}
                                  </li>
                                ))}
                            </ul>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default GoalDashPodDetail;
